import React from 'react';
import { Power } from 'lucide-react';

// Single beat (quarter) button: 4 states off / click / beat / high accent.
const BEAT_CLASSES = {
  0: 'bg-[#0f0f10] border-white/10 hover:border-white/30 text-white/30',
  1: 'bg-pink-500/10 border-pink-500/60 text-pink-300 shadow-[0_0_18px_rgba(236,72,153,0.25)] hover:bg-pink-500/20',
  2: 'bg-emerald-500/15 border-emerald-400/70 text-emerald-300 shadow-[0_0_22px_rgba(16,185,129,0.4)] hover:bg-emerald-500/20',
  3: 'bg-sky-500/15 border-sky-400/70 text-sky-200 shadow-[0_0_22px_rgba(56,189,248,0.45)] hover:bg-sky-500/20',
};

export const BeatCell = React.memo(function BeatCell({
  beatIndex,
  state,
  isCurrent,
  disabled,
  onCycle,
}) {
  const base =
    'relative h-12 md:h-14 w-full rounded-md border transition-all duration-100 flex flex-col items-center justify-center cursor-pointer select-none overflow-hidden focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:outline-none';
  const stateCls = disabled
    ? 'bg-[#0a0a0a] border-white/5 text-white/15 cursor-not-allowed opacity-50'
    : BEAT_CLASSES[state] ?? BEAT_CLASSES[0];

  const playhead = isCurrent
    ? 'ring-2 ring-white scale-[1.06] brightness-150 z-10'
    : '';

  return (
    <button
      type="button"
      data-testid={`beat-cell-${beatIndex}`}
      aria-label={`Quarto ${beatIndex + 1}, stato ${state}`}
      disabled={disabled}
      onClick={() => !disabled && onCycle(beatIndex)}
      className={`${base} ${stateCls} ${playhead}`}
    >
      <span className="font-bebas text-xl md:text-2xl leading-none tabular-nums">
        {beatIndex + 1}
      </span>
      <span className="text-[9px] tracking-widest uppercase opacity-70">
        {state === 0 ? 'off' : state === 1 ? 'on' : state === 2 ? 'beat' : 'high'}
      </span>
    </button>
  );
});

// Bar enable/disable luminous on/off button.
export const BarToggle = React.memo(function BarToggle({
  barIndex,
  enabled,
  isCurrentBar,
  onToggle,
}) {
  const base =
    'relative h-12 md:h-14 w-12 md:w-14 rounded-full border transition-all duration-150 flex flex-col items-center justify-center cursor-pointer select-none focus-visible:ring-2 focus-visible:ring-pink-400 focus-visible:outline-none shrink-0';
  const stateCls = enabled
    ? 'bg-pink-500/15 border-pink-400 text-pink-200 shadow-[0_0_22px_rgba(236,72,153,0.55)] hover:bg-pink-500/25'
    : 'bg-[#0a0a0a] border-white/15 text-white/35 hover:border-white/40';
  const playhead = isCurrentBar
    ? 'ring-2 ring-white scale-[1.08] brightness-125 z-10'
    : '';

  return (
    <button
      type="button"
      data-testid={`bar-toggle-${barIndex}`}
      aria-pressed={enabled}
      aria-label={`Battuta ${barIndex + 1} ${enabled ? 'attiva' : 'disattiva'}`}
      onClick={() => onToggle(barIndex)}
      className={`${base} ${stateCls} ${playhead}`}
      title={`Battuta ${barIndex + 1}`}
    >
      <Power className="w-3.5 h-3.5" />
      <span className="font-bebas text-sm leading-none tabular-nums mt-0.5">
        {barIndex + 1}
      </span>
    </button>
  );
});

// Renders the per-bar beat pattern (within ONE bar) — defines the click pattern.
export function BeatPatternRow({ pattern, currentBeat, isPlaying, currentBarEnabled, onCycleBeat }) {
  return (
    <div data-testid="beat-pattern-row" className="grid gap-2 md:gap-3" style={{ gridTemplateColumns: `repeat(${Math.max(pattern.length, 1)}, minmax(0, 1fr))` }}>
      {pattern.map((state, i) => (
        <BeatCell
          key={i}
          beatIndex={i}
          state={state}
          isCurrent={isPlaying && currentBeat === i}
          disabled={isPlaying && currentBarEnabled === false}
          onCycle={onCycleBeat}
        />
      ))}
    </div>
  );
}

// Renders the bar grid (up to 32 bars).
export function BarGrid({ barsEnabled, currentBar, isPlaying, onToggleBar }) {
  return (
    <div
      data-testid="bar-grid"
      className="flex flex-wrap gap-2 md:gap-3"
    >
      {barsEnabled.map((on, i) => (
        <BarToggle
          key={i}
          barIndex={i}
          enabled={on}
          isCurrentBar={isPlaying && currentBar === i}
          onToggle={onToggleBar}
        />
      ))}
    </div>
  );
}
